import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { friendsApi, joinInvite } from "@/lib/friends";
import { playSfx } from "@/lib/audio";
import "./Friends.css";
// Global: checks for game invites every 15 s and shows a parchment pop-up.
const MODE_LABEL = {
  classic: "Classic Chess", tmc: "Two Move Chess", checkers: "Checkers",
  extended_classic: "Extended Classic", extended_tmc: "Extended Two Move",
  ctc: "Capture the Crown", br: "Battle Royale", lok: "Legend of Kings", ctk: "Capture the King",
  fpc: "Four Player Chess", fpc_team: "2v2 Team Chess", fpc_1v3: "1v3 Team Chess",
  extended_fpc: "Extended Four Player Chess", extended_fpc_team: "Extended 2v2 Team Chess",
  extended_fpc_1v3: "Extended 1v3 Team Chess", four_way_checkers: "Four-Way Checkers",
  faction_wars: "Faction Wars",
};
export default function InviteWatcher() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!user) return undefined;
    let alive = true;
    const seen = new Set();
    const check = async () => {
      try {
        const list = await friendsApi.invites();
        const fresh = (list || []).find((i) => !seen.has(i.id));
        if (alive && fresh) {
          seen.add(fresh.id);
          setInvite(fresh);
          setError("");
          playSfx("match_start");
        }
      } catch { /* signed out or offline */ }
    };
    check();
    const id = setInterval(check, 15000);
    return () => { alive = false; clearInterval(id); };
  }, [user]);
  if (!invite) return null;
  const join = async () => {
    setBusy(true);
    setError("");
    try {
      const gid = await joinInvite(invite, user?.name);
      setInvite(null);
      navigate(invite.game_mode === "faction_wars" ? `/faction-wars/${gid}` : `/game/${gid}`);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Could not join");
    }
    setBusy(false);
  };
  const decline = async () => {
    try { await friendsApi.answerInvite(invite.id, false); } catch { /* ignore */ }
    setInvite(null);
  };
  return (
    <div className="fr-invite" role="alertdialog" aria-label="Game invite" data-testid="invite-popup">
      <div className="fr-invite-seal" aria-hidden="true">⚔</div>
      <div className="fr-invite-text">
        <b>{invite.from_name}</b> challenges you to <b>{MODE_LABEL[invite.game_mode] || invite.game_mode}</b>
      </div>
      {error && <div className="fr-msg">{error}</div>}
      <div className="fr-invite-actions">
        <button type="button" className="fr-btn fr-btn--gold" disabled={busy} onClick={join} data-testid="invite-join">Join</button>
        <button type="button" className="fr-btn" disabled={busy} onClick={decline} data-testid="invite-decline">Decline</button>
      </div>
    </div>
  );
}
